'use client';

import { useEffect, useState } from 'react';

// Random, non-overlapping star pattern that anchors the Votely visual
// identity. Extracted from app/page.tsx so non-homepage routes (group flow,
// account, history) can match the brand language.
//
// Stars are generated once per mount via useMemo with a stable salt - this
// keeps re-renders cheap and doesn't shuffle stars while you interact with
// the page. Each navigation gets a fresh pattern, which feels alive without
// being distracting.

interface Star {
  top: number;
  left: number;
  size: string;
  color: string;
  rotate: string;
}

const SIZES = [
  'w-8 h-8', 'w-10 h-10', 'w-12 h-12', 'w-14 h-14', 'w-16 h-16', 'w-20 h-20',
  'w-24 h-24', 'w-28 h-28', 'w-32 h-32', 'w-40 h-40',
];
const SIZES_SUBTLE = [
  'w-4 h-4', 'w-5 h-5', 'w-6 h-6', 'w-8 h-8', 'w-10 h-10', 'w-12 h-12',
];
const COLORS = ['text-primary', 'text-primary-light'];
const COLORS_SUBTLE = ['text-slate-300', 'text-slate-200'];
const ROTATIONS = [
  'rotate-0', 'rotate-3', 'rotate-6', 'rotate-12',
  '-rotate-3', '-rotate-6', '-rotate-12', 'rotate-45', '-rotate-45',
];

function makePattern(numStars: number, minDist: number, biasTop: boolean, subtle = false): Star[] {
  const sizes = subtle ? SIZES_SUBTLE : SIZES;
  const colors = subtle ? COLORS_SUBTLE : COLORS;
  const pattern: Star[] = [];
  const tryPlace = (existing: Star[]): Star => {
    for (let attempt = 0; attempt < 100; attempt++) {
      const top = Math.random() * 95 + 2;
      const left = Math.random() * 95 + 2;
      const tooClose = existing.some((s) => {
        const dx = s.left - left;
        const dy = s.top - top;
        return Math.sqrt(dx * dx + dy * dy) < minDist;
      });
      if (!tooClose) {
        return {
          top, left,
          size: sizes[Math.floor(Math.random() * sizes.length)] + ' md:' + sizes[Math.floor(Math.random() * sizes.length)],
          color: colors[Math.floor(Math.random() * colors.length)],
          rotate: ROTATIONS[Math.floor(Math.random() * ROTATIONS.length)],
        };
      }
    }
    return {
      top: Math.random() * 95 + 2,
      left: Math.random() * 95 + 2,
      size: sizes[Math.floor(Math.random() * sizes.length)] + ' md:' + sizes[Math.floor(Math.random() * sizes.length)],
      color: colors[Math.floor(Math.random() * colors.length)],
      rotate: ROTATIONS[Math.floor(Math.random() * ROTATIONS.length)],
    };
  };

  for (let i = 0; i < numStars; i++) {
    const wantTop = biasTop && i < Math.floor(numStars * 2 / 3);
    let star = tryPlace(pattern);
    let tries = 0;
    while (
      pattern.some((s) => Math.sqrt((s.left - star.left) ** 2 + (s.top - star.top) ** 2) < minDist) &&
      tries < 10
    ) {
      star = tryPlace(pattern);
      if (wantTop && star.top > 50) star.top = Math.random() * 45 + 2;
      tries++;
    }
    pattern.push(star);
  }
  return pattern;
}

interface StarBackgroundProps {
  /** Number of stars to scatter. Default 110 (homepage density). */
  density?: number;
  /** Bias placement toward the top half (matches homepage hero). */
  biasTop?: boolean;
  /** Minimum distance between stars in % units. */
  minDist?: number;
  /** Opacity multiplier (0-1). Default 0.8. */
  opacity?: number;
  /** Use subtle variant: fewer, smaller, slate-colored stars for professional pages. */
  variant?: 'default' | 'subtle';
}

export default function StarBackground({
  density,
  biasTop = true,
  minDist = 7,
  opacity,
  variant = 'default',
}: StarBackgroundProps) {
  const isSubtle = variant === 'subtle';
  const resolvedDensity = density ?? (isSubtle ? 30 : 110);
  const resolvedOpacity = opacity ?? (isSubtle ? 0.06 : 0.8);

  const [stars, setStars] = useState<Star[]>([]);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot client-only generation, mirrors LocaleProvider pattern
    setStars(makePattern(resolvedDensity, minDist, biasTop, isSubtle));
  }, [resolvedDensity, minDist, biasTop, isSubtle]);

  return (
    <div className="fixed inset-0 pointer-events-none" aria-hidden="true">
      {stars.map((star, i) => (
        <svg
          key={i}
          className={`absolute ${star.size} ${star.color} ${star.rotate}`}
          style={{
            top: `${star.top}%`,
            left: `${star.left}%`,
            opacity: resolvedOpacity,
          }}
          fill="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
        >
          <polygon points="12,2 15,10 23,10 17,15 19,23 12,18 5,23 7,15 1,10 9,10" />
        </svg>
      ))}
    </div>
  );
}
